import React, { useState } from "react";
import {
    Box,
    IconButton,
    Popover,
    Stack,
    Typography,
    SxProps,
    Theme,
} from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { formatDateForDisplay } from "@moment-golf/shared";

interface EarlyRegPriceDisplayProps {
    currentPriceLabel: string;
    regularPriceLabel: string;
    cutoffDate: Date | string | null;
    earlyRegActive: boolean;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    typographySx?: SxProps<Theme>;
    iconSize?: "small" | "medium";
    typographyVariant?: "subtitle1" | "subtitle2" | "h6";
}

export const EarlyRegPriceDisplay = ({
    currentPriceLabel,
    regularPriceLabel,
    cutoffDate,
    earlyRegActive,
    prefix,
    suffix,
    typographySx,
    iconSize = "small",
    typographyVariant = "subtitle1",
}: EarlyRegPriceDisplayProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleIconClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(anchorEl ? null : e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                flexWrap="wrap"
                sx={{ display: "inline-flex", verticalAlign: "middle" }}
            >
                {prefix}
                {earlyRegActive && (
                    <IconButton
                        size={iconSize}
                        onClick={handleIconClick}
                        aria-label="Early registration pricing details"
                        sx={{
                            color: "primary.main",
                            p: 0.25,
                            width: iconSize === "small" ? 26 : 32,
                            height: iconSize === "small" ? 26 : 32,
                            flexShrink: 0,
                            backgroundColor: "grey.200",
                            borderRadius: "50%",
                            "&:hover": {
                                backgroundColor: "grey.300",
                            },
                        }}
                    >
                        <LocalOfferOutlinedIcon sx={{ fontSize: iconSize === "small" ? 18 : 22 }} />
                    </IconButton>
                )}
                <Typography
                    variant={typographyVariant}
                    sx={{ fontWeight: 500, ...typographySx }}
                >
                    {currentPriceLabel}
                </Typography>
                {suffix}
            </Stack>
            {earlyRegActive && (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    onClick={(e) => e.stopPropagation()}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                    <Box sx={{ p: 2, maxWidth: 280 }}>
                        <Stack spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Early Registration Pricing
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Regular price: {regularPriceLabel}
                            </Typography>
                            {cutoffDate && (
                                <Typography variant="body2" color="text.secondary">
                                    Register before {formatDateForDisplay(cutoffDate)}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Popover>
            )}
        </>
    );
};
